/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { If, Then, Else, When } from "react-if";
import {
  Box,
  PieChart,
  Text,
  theme,
  FlexContainer,
  SVGIcon,
  ExtendedTooltip,
} from "@nordcloud/gnui";
import { dateFormat, NO_FORECAST_TEXT } from "~/constants";
import { useCurrency } from "~/hooks";
import { periodDayJs } from "~/services/customers";
import { FormatMoney } from "~/tools";
import { usePercentageDifference } from "./usePercentageDifference";
import { formatNumber, getPieChartTextSize } from "./utils";

type ForecastWidgetProps = {
  showForecast: boolean;
  forecast?: number;
  currentMonth: number;
  previous: number;
};

export function ForecastWidget({
  showForecast,
  forecast = -1,
  currentMonth,
  previous,
}: ForecastWidgetProps) {
  const { currency } = useCurrency();

  // At the start of the month forecasts are not ready (and possibly other costs), so we get “-1” from BE
  const isValidForecast = forecast !== -1;

  const showData = showForecast ? isValidForecast : true;
  const current = showForecast ? forecast : currentMonth;

  const {
    percentageDifference,
    isFinite: showComparisonChart,
    progress,
    quantifier,
  } = usePercentageDifference(previous, current);

  const title = `${showForecast ? "Forecast " : "Cost "}
              ${periodDayJs().format(dateFormat.monthYear)}`;

  const displayPercentageValue = formatNumber(percentageDifference);
  const pieChartTextSize = getPieChartTextSize(displayPercentageValue);

  return (
    <FlexContainer>
      <When condition={showData && showComparisonChart}>
        <PieChart
          progress={progress}
          size={96}
          strokeWidth={15}
          color={quantifier === "less" ? "success" : "danger"}
          mr="1rem"
          data-testid="forecast-widget-diff-chart"
        >
          <Text
            className="chartValue"
            size={pieChartTextSize.value}
            tag="span"
            weight="medium"
          >
            {displayPercentageValue}
            <Text
              className="chartValue"
              tag="span"
              weight="medium"
              size={pieChartTextSize.symbol}
            >
              %
            </Text>
          </Text>
          <Text className="chartCaption" size="xs" tag="span">
            {quantifier}
          </Text>
        </PieChart>
      </When>
      <Box shadow="shadow00" p={0}>
        <If condition={showData}>
          <Then>
            <Text size="sm" tag="div" color={theme.color.text.text02}>
              {title}
            </Text>
            <Text size="xxl" tag="div" weight="medium">
              <FormatMoney value={current} currency={currency} />
            </Text>
          </Then>
          <Else>
            <FlexContainer direction="column" alignItems="flex-start">
              <Text size="sm">{title}</Text>
              <ExtendedTooltip caption={NO_FORECAST_TEXT}>
                <SVGIcon
                  name="minus"
                  size="sm"
                  data-testid="forecast-display"
                />
              </ExtendedTooltip>
            </FlexContainer>
          </Else>
        </If>
        <When condition={showData}>
          <Text tag="div" weight="medium">
            <FormatMoney
              value={Math.abs(current - previous)}
              currency={currency}
            />
            <Text size="sm" tag="span" px={theme.spacing.spacing01}>
              {quantifier} than
            </Text>
            <Text size="sm" tag="span">
              {periodDayJs().subtract(1, "month").format(dateFormat.monthYear)}
            </Text>
          </Text>
        </When>
      </Box>
    </FlexContainer>
  );
}
